/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getDataBool(key, defaultValue = true) {
  const v = this.dataset?.[key];
  if (v == null) return defaultValue;          // attribute missing
  if (v === '' || v === 'true') return true;   // present with no value OR "true"
  if (v === 'false') return false;
  return defaultValue;
}

if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.slideshow = this.querySelector('.slideshow');
      this.slides = this.querySelectorAll('.slideshow__slide');
      this.nav = this.querySelector('.slideshow-nav');
      this.pagination = this.querySelector('.slideshow-pagination');
      this.rtl = document.dir === 'rtl';
      this.currentIndex = 0;
      this.swipeThreshold = 80;
      this.wheelTriggerWait = 800;
      this.transitionOutDelay = this.dataset.transitionOutDelay || 0;
      // For smoother scrolling animation
      this.dataset.smoothScroll = getDataBool('smoothScroll', true);
      if (this.dataset.smoothScroll) {
        this.gsap = window.gsap;
        if (this.gsap && window.ScrollToPlugin) {
          this.gsap.registerPlugin(window.ScrollToPlugin);
        }
      }

      if (this.nav) {
        this.counterCurrent = this.querySelector('.slideshow-nav__counter-current');
        this.nav.addEventListener('click', this.handleNavClick.bind(this));
      }

      if (this.pagination) {
        this.pageBtns = this.querySelectorAll('.page-btn');
        this.videoStatus = this.querySelector('[data-video-status]');
        this.pagination.addEventListener('click', this.handlePaginationClick.bind(this));

        // Keep a video slide's page-btn in sync with whether its video is actually playing or
        // paused (however that came about - this dot's own toggle, or the viewport observer
        // in play-video-in-view.js auto-pausing it when scrolled away). 'play'/'pause' don't
        // bubble, so this listens during the capturing phase instead (matches
        // play-video-in-view.js's own document-level listener for the same reason).
        this.slideshow.addEventListener('play', this.handleVideoStateChange.bind(this), true);
        this.slideshow.addEventListener('pause', this.handleVideoStateChange.bind(this), true);
      }

      if (theme.device.hasTouch) {
        this.slideshow.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
        this.slideshow.addEventListener('touchmove', debounce(this.handleTouchMove.bind(this), 200), { passive: true });
      }

      this.slideshow.addEventListener('wheel', this.handleWheel.bind(this));

      this.slideshow.addEventListener('transitionend', SlideShow.handleTransitionend);

      this.saveSlideVisibility();
      this.setSlideVisibility();
      if (this.dataset.autoplay === 'true') this.initAutoPlay();
    }

    initAutoPlay() {
      this.autoplayEnabled = true;
      this.autoplaySpeed = this.dataset.speed * 1000;
      this.pauseAutoplayOnMouseover = this.dataset.pauseAutoplayOnMouseover !== 'false';

      if (this.pauseAutoplayOnMouseover) {
        this.addEventListener('mouseover', this.pauseAutoplay.bind(this));
        this.addEventListener('mouseleave', this.resumeAutoplay.bind(this));
      }

      this.slideshow.addEventListener('focusin', this.pauseAutoplay.bind(this));
      this.slideshow.addEventListener('focusout', this.resumeAutoplay.bind(this));

      this.addObserver();
      this.startAutoplay();
    }

    /**
     * Handles 'click' events on the nav buttons container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (evt.target.matches('.autoplay-btn')) {
        this.togglePlayState(evt);
        return;
      }

      if (evt.target.name === 'next') {
        this.showNextSlide();
      } else if (evt.target.name === 'prev') {
        this.showPrevSlide();
      }
    }

    /**
     * Handles 'click' events on the pagination.
     * @param {object} evt - Event object.
     */
    handlePaginationClick(evt) {
      if (!evt.target.matches('.page-btn')) return;
      this.autoplayTimeLeft = this.autoplaySpeed;

      const targetIndex = Number(evt.target.dataset.index);
      this.setActiveSlide(targetIndex);

      // Toggle this slide's video when its own page-btn is clicked - play/pause, whichever
      // it isn't currently doing.
      if (evt.target.dataset.videoTrigger != null) {
        const videoSlide = this.slides[targetIndex];
        const videoComponent = videoSlide && videoSlide.querySelector('video-component');
        if (videoComponent) videoComponent.toggle();
      }
    }

    /**
     * Handles 'play'/'pause' events bubbling (via capture) from a slide's video, keeping its
     * page-btn's 'is-paused' class (see main.css's .page-btn__video-icon) in sync, and
     * announcing the new state via a shared live region (not nested inside the button itself -
     * that would fold this text into its accessible name on every update, rather than
     * announcing it as a standalone status).
     * @param {object} evt - Event object.
     */
    handleVideoStateChange(evt) {
      if (evt.target.tagName !== 'VIDEO') return;

      const slide = evt.target.closest('.slideshow__slide');
      const slideIndex = Array.prototype.indexOf.call(this.slides, slide);
      const pageBtn = this.pageBtns[slideIndex];
      if (!pageBtn) return;

      pageBtn.classList.toggle('is-paused', evt.target.paused);

      if (this.videoStatus) {
        this.videoStatus.textContent = evt.target.paused
          ? this.dataset.textVideoPaused
          : this.dataset.textVideoPlaying;
      }
    }

    /**
     * Handles 'wheel' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleWheel(evt) {
      if (this.blockWheelTrigger) {
        evt.preventDefault();
      } else if (evt.deltaX > 20) {
        evt.preventDefault();
        this.showNextSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      } else if (evt.deltaX < -20) {
        evt.preventDefault();
        this.showPrevSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      }
    }

    /**
     * Handles 'touchstart' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchStart(evt) {
      this.touchStart = evt.changedTouches[0].screenX;
    }

    /**
     * Handles 'touchmove' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchMove(evt) {
      const touchEnd = evt.changedTouches[0].screenX;

      if (touchEnd < this.touchStart - this.swipeThreshold) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      }

      if (touchEnd > this.touchStart + this.swipeThreshold) {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    /**
     * Handles 'transitionend' events on the slideshow.
     * @param {object} evt - Event object.
     */
    static handleTransitionend(evt) {
      if (!evt.target.matches('.slideshow__motion-media')) return;
      evt.target.classList.remove('transition-out');
    }

    showNextSlide() {
      let index = this.currentIndex + 1;
      if (index === this.slides.length) index = 0;
      this.setActiveSlide(index, 'next');
    }

    showPrevSlide() {
      let index = this.currentIndex - 1;
      if (index < 0) index = this.slides.length - 1;
      this.setActiveSlide(index, 'prev');
    }

    /**
     * Sets the active slide.
     * @param {number} slideIndex - Index of the slide to show.
     * @param {string} slideDirection - Direction for slide transitions.
     */
    setActiveSlide(slideIndex, slideDirection) {
      if (slideIndex === this.currentIndex) return;
      // Reset any prior transitions.
      this.querySelectorAll('.transition-out').forEach((el) => el.classList.remove('transition-out'));

      // Set data attribute for direction of slide transitions.
      let dir = slideDirection || (slideIndex < this.currentIndex ? 'prev' : 'next');
      if (this.rtl) dir = dir === 'next' ? 'prev' : 'next';
      this.dataset.direction = dir;

      // Trigger text overlay transition out.
      this.slides[this.currentIndex].classList.add('transition-out');

      setTimeout(() => {
        // Accomodates the smooth slide setting on the slideshow
        const behavior = this.dataset.transition === 'slide' ? 'smooth' : 'instant';

         if(this.dataset.smoothScroll) {
          if(this.dataset.mode == 'center') {
            const container = this;
            const currentSlide = this.slides[slideIndex];
            const slideRect = currentSlide.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const maxScroll = container.scrollWidth - container.clientWidth;

            const viewportWidth = this.getBoundingClientRect().width;
            const slideWidth = this.slides[slideIndex].getBoundingClientRect().width;
            let offset = viewportWidth - slideWidth;
            this.gsap.to(this.slideshow, {
              scrollTo: {
                  x: this.slides[slideIndex].offsetLeft - (offset / 2)
                },
                duration: 2.2,
                ease: "power4.out"
            });

          } else {

            if (this.gsap) {
              this.gsap.to(this.slideshow, {
                  scrollTo: {
                    x: this.slides[slideIndex].offsetLeft
                  },
                  duration: 2.2,
                  ease: "power4.out"
              });
            } else {
              this.slideshow.scrollTo({ left: this.slides[slideIndex].offsetLeft, behavior: behavior });
            }
          }
            
         } else {
            this.slideshow.scrollTo({ left: this.slides[slideIndex].offsetLeft, behavior: behavior });
         }

        // Move new slide into the viewport.
        // if(this.dataset.smoothScroll) {
        //   const container = this;
        //   const currentSlide = this.slides[slideIndex];
        //   const slideRect = currentSlide.getBoundingClientRect();
        //   const containerRect = container.getBoundingClientRect();
        //   let offset = 
        //     (slideRect.left - containerRect.left) +
        //     (slideRect.width / 2 - containerRect.width / 2);
        //   const maxScroll = container.scrollWidth - container.clientWidth;

        //   const viewportWidth = this.getBoundingClientRect().width;
        //   const slideWidth = this.slides[slideIndex].getBoundingClientRect().width;
        //   offset = viewportWidth - slideWidth;


        // }

        // Update current slide and trigger transitions.
        this.slides[slideIndex].classList.add('is-active');
        this.slides[this.currentIndex].classList.remove('is-active', 'transition-out');

        // Add class to transitioned content element (if it exists).
        const transitionedEl = this.slides[this.currentIndex].querySelector('.slideshow__motion-media');
        if (transitionedEl) transitionedEl.classList.add('transition-out');

        // Reset autoplay.
        if (this.autoplayEnabled) {
          if (this.autoplayPaused) {
            this.autoplayStartTime = Date.now();
          } else {
            clearTimeout(this.autoplayTimer);
            this.startAutoplay();
          }
        }

        // Update slide counter.
        if (this.counterCurrent) this.counterCurrent.textContent = slideIndex + 1;

        this.currentIndex = slideIndex;
        this.setSlideVisibility();
        this.updatePagination();
      }, this.transitionOutDelay);
    }

    /**
     * Updates the 'aria-current' attribute of the pagination buttons.
     */
    updatePagination() {
      if (!this.pagination) return;

      this.pageBtns.forEach((el, index) => {
        if (index === this.currentIndex) {
          el.setAttribute('aria-current', 'true');
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }

    stopAutoplay() {
      this.setSlideshowState('paused');
      clearTimeout(this.autoplayTimer);
    }

    startAutoplay() {
      this.autoplayTimeLeft = null;
      this.autoplayStartTime = Date.now();
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
      this.setSlideshowState('running');
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.stopAutoplay();

      if (this.autoplayTimeLeft) {
        this.autoplayTimeLeft -= (Date.now() - this.resumedTime);
      } else {
        this.autoplayTimeLeft = this.autoplaySpeed - (Date.now() - this.autoplayStartTime);
      }
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled || !this.autoplayPaused) return;

      this.resumedTime = Date.now();
      this.autoplayTimer = setTimeout(() => {
        this.showNextSlide();
        this.startAutoplay();
      }, this.autoplayTimeLeft);

      this.setSlideshowState('running');
    }

    /**
     * Adds an observer to pause autoplay when the slideshow is not in the viewport.
     */
    addObserver() {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumeAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      });

      observer.observe(this);
    }

    /**
     * Saves initial 'aria-hidden' and 'tabindex' of links in slides.
     */
    saveSlideVisibility() {
      this.slides.forEach((slide) => {
        slide.querySelectorAll('a[tabindex]').forEach((link) => {
          link.dataset.dataSlideshowPriorTabindex = link.getAttribute('tabindex');
        });
      });
    }

    /**
     * Sets 'aria-hidden' and 'tabindex' of the slides and text overlay buttons.
     */
    setSlideVisibility() {
      this.slides.forEach((el, index) => {
        const links = el.querySelectorAll('a');

        if (index === this.currentIndex) {
          el.setAttribute('aria-hidden', 'false');
          el.removeAttribute('tabindex');

          links.forEach((link) => {
            if (link.dataset.dataSlideshowPriorTabindex !== undefined) {
              link.setAttribute('tabindex', link.dataset.dataSlideshowPriorTabindex);
            } else {
              link.removeAttribute('tabindex');
            }
          });
        } else {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');

          links.forEach((link) => {
            link.setAttribute('tabindex', '-1');
          });
        }
      });
    }

    /**
     * Sets 'aria-live' state of the slideshow and 'play-state' of the pagination animation.
     * @param {string} state - State to set ('running' or 'paused').
     */
    setSlideshowState(state) {
      this.style.setProperty('--play-state', state);
      this.slideshow.setAttribute('aria-live', state === 'running' ? 'off' : 'polite');
      this.autoplayPaused = state === 'paused';
    }

    /**
     * Toggles the autoplay state.
     * @param {object} evt - Event object.
     */
    togglePlayState(evt) {
      evt.target.classList.toggle('is-paused');
      this.autoplayEnabled = !this.autoplayEnabled;
      this.dataset.autoplay = this.autoplayEnabled ? 'true' : 'false';
      this.autoplayEnabled ? this.startAutoplay() : this.stopAutoplay();
    }
  }

  customElements.define('slide-show', SlideShow);
}
